/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react'
import style from './campaign.module.css'
import { Card, List, Typography, Row, Col, Button, Space, Select, Tag } from 'antd';
import { ArrowRightOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Pie, PieChart, Cell, Legend } from 'recharts';
import { MdOutlineAnalytics, MdOutlineCampaign } from 'react-icons/md';
import { BsDot } from "react-icons/bs";
import { LuFileText, LuBox } from "react-icons/lu";
import { MdOutlineCancel } from "react-icons/md";
import { FiDownload } from "react-icons/fi";
import { GrAtm } from "react-icons/gr";
import { FaRegEye } from "react-icons/fa";
import { PiUserCircleFill } from "react-icons/pi";
import { FaMobile } from "react-icons/fa";

const { Text, Title } = Typography;
const { Option } = Select;

const data2 = [
    { name: 'National Day', value: 310, color: '#0088FE' },
    { name: 'Eid Celebrations', value: 217, color: '#00C49F' },
    { name: 'New Year', value: 351, color: '#7161ef' },
];

const datapie = [
    { name: "ATM", value: 2992 },
    { name: "POS", value: 8243 },
    { name: "Mobile Banking", value: 5672 },
    { name: "Bill Payment", value: 8243 }
];

const transactions = [
    { icon: <GrAtm size={20} color="#3fd0c9" />, title: 'ATM Withdrawal Processed', id: '#12OQKL' },
    { icon: <MdOutlineCancel size={20} color="#ff7eb3" />, title: 'Bill Payment', description: '$150 failed customer (1002)', id: '#24CCML' },
    { icon: <LuBox size={20} color="#3884ff" />, title: 'Funds Transfer Successful', id: '#01QAMB' },
    { icon: <FiDownload size={20} color="#7161ef" />, title: 'Mobile Recharge Processed', id: '#08OMLB' },
    { icon: <GrAtm size={20} color="#3fd0c9" />, title: 'Balance Inquiry Completed', description: 'Account ***1225', id: '#14OFKS' },
    { icon: <MdOutlineCancel size={20} color="#ff758c" />, title: 'Electricity Bill Payment Confirmed', id: '#20ITHP' },
    { icon: <FiDownload size={20} color="#3884ff" />, title: 'Subscription Payment Processed', id: '#08OFGK' },
];

interface CampaignData {
    title: string;
    amount: string;
    percentageChange: string;
    transactions: number;
    lastMonth: string;
    iconColor: string;
    icon: React.ReactNode;
    bgColor: string;
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const TransactionCampaign: React.FC = () => {
    const [campaigns, setCampaigns] = useState<CampaignData[]>([]);

    useEffect(() => {
        setCampaigns([
            {
                title: "ATM",
                amount: "OMR 2,992",
                percentageChange: "19%",
                transactions: 1455,
                lastMonth: "Last Month",
                iconColor: "#095179",
                bgColor: "#e8f4fd",
                icon: <LuFileText size={20} color='#095179' />
            },
            {
                title: "POS",
                amount: "OMR 5,672",
                percentageChange: "12%",
                transactions: 2350,
                lastMonth: "Last Month",
                iconColor: "#1a9e5c",
                bgColor: "#e6f9f0",
                icon: <FaRegEye size={20} color='#1a9e5c' />
            },
            {
                title: "Bill Payment",
                amount: "OMR 8,243",
                percentageChange: "25%",
                transactions: 3255,
                lastMonth: "Last Month",
                iconColor: "#c58804",
                bgColor: "#fff8e1",
                icon: <PiUserCircleFill size={20} color='#c58804' />
            },
            {
                title: "Mobile Banking",
                amount: "OMR 8,243",
                percentageChange: "25%",
                transactions: 3255,
                lastMonth: "Last Month",
                iconColor: "#7161ef",
                bgColor: "#f0eeff",
                icon: <FaMobile size={20} color='#7161ef' />
            },
        ]);
    }, []);

    const totalPie = datapie.reduce((acc, cur) => acc + cur.value, 0);

    return (
        <div className={style.container}>

            {/* Page Header */}
            <div className={style.pageHeader}>
                <div>
                    <Title className={style.pageTitle}>Dashboard</Title>
                    <div className={style.pageBreadcrumb}>
                        Home / <span>Dashboard</span>
                    </div>
                </div>
                <Text type="secondary" style={{ fontSize: 13 }}>
                    {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </Text>
            </div>

            {/* Row 1: [2x2 boxes] | [Pie Chart] | [Recent Transactions] */}
            <div className={style.statsRow}>

                {/* 2x2 stat boxes */}
                <div className={style.statsGrid}>
                    {campaigns.map((campaign, index) => (
                        <Card key={index} className={style.statCard} bodyStyle={{ padding: '16px 20px' }}>
                            <div className={style.statCardInner}>
                                <div className={style.cardTitle}>
                                    <div className={style.iconBox} style={{ background: campaign.bgColor }}>
                                        {campaign.icon}
                                    </div>
                                    <Text strong style={{ fontSize: 13, color: '#5a5f7d' }}>{campaign.title}</Text>
                                </div>
                                <Title className={style.statAmount}>{campaign.amount}</Title>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    <span className={style.numTitle}><ArrowUpOutlined /> {campaign.percentageChange}</span>
                                    <span className={style.lastM}>{campaign.lastMonth}</span>
                                </div>
                                <Text className={style.Text}>
                                    Transactions: <strong style={{ color: '#1a1f36' }}>{campaign.transactions.toLocaleString()}</strong>
                                </Text>
                                <Button
                                    type="link"
                                    className={style.btn}
                                    onClick={() => alert(`Viewing details for ${campaign.title}`)}
                                >
                                    View Details <ArrowRightOutlined />
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Channel Wise Analytics */}
                <Card className={style.chartCard} bodyStyle={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Header */}
                    <div className={style.chartCardHeader}>
                        <div className={style.chartCardTitle}>
                            <MdOutlineAnalytics size={20} color="#095179" />
                            Channel Wise Analytics
                        </div>
                        <Select defaultValue="lastMonth" style={{ width: 120 }} size="small">
                            <Option value="lastMonth">Last month</Option>
                            <Option value="thisMonth">This month</Option>
                        </Select>
                    </div>
                    {/* Legend 2x2 grid */}
                    <div className={style.legendGrid}>
                        {datapie.map((item, i) => (
                            <div key={i} className={style.legendItem}>
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i], display: 'inline-block', flexShrink: 0 }} />
                                <span>{item.name}</span>
                                <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#1a1f36' }}>{((item.value / totalPie) * 100).toFixed(1)}%</span>
                            </div>
                        ))}
                    </div>
                    {/* Centered Pie */}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Tooltip formatter={(value: number) => [`${((value / totalPie) * 100).toFixed(1)}%`, '']} />
                                <Pie
                                    data={datapie}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={75}
                                    outerRadius={110}
                                    paddingAngle={4}
                                    dataKey="value"
                                >
                                    {datapie.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                {/* Recent Transactions */}
                <Card
                    className={style.recentCard}
                    title={
                        <Row align="middle" style={{ gap: 8 }}>
                            <LuBox size={18} color="#095179" />
                            <Text strong style={{ fontSize: 15, color: '#1a1f36' }}>Recent Transactions</Text>
                        </Row>
                    }
                    extra={<Button type="link" style={{ color: '#095179', fontWeight: 600, fontSize: 13 }}>View All</Button>}
                    bodyStyle={{ padding: '0 16px' }}
                >
                    <List
                        itemLayout="horizontal"
                        dataSource={transactions}
                        renderItem={(item) => (
                            <List.Item style={{ padding: '10px 0', borderBottom: '1px solid #f4f6fb' }}>
                                <div className={style.content}>
                                    <div className={style.txIconBox}>{item.icon}</div>
                                    <div style={{ flex: 1 }}>
                                        <Text className={style.txTitle}>{item.title}</Text>
                                        {item.description && <Text style={{ fontSize: 11, color: '#8c8fa3', display: 'block' }}>{item.description}</Text>}
                                        <Text className={style.txId}>{item.id}</Text>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                </Card>
            </div>

            {/* Row 2: Total Campaigns full width */}
            <div className={style.mainRow}>
                <Card className={style.chartCard} bodyStyle={{ padding: '20px 24px' }}>
                    <div className={style.chartCardHeader}>
                        <div className={style.chartCardTitle}>
                            <MdOutlineCampaign size={20} color="#095179" />
                            Total Campaigns
                        </div>
                        <Select defaultValue="3m" style={{ width: 110 }} size="small">
                            <Option value="3m">3 months</Option>
                            <Option value="6m">6 months</Option>
                            <Option value="12m">12 months</Option>
                        </Select>
                    </div>
                    <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                        {data2.map((item, i) => (
                            <div key={i} style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#8c8fa3', marginBottom: 2 }}>{item.name}</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}K</div>
                            </div>
                        ))}
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data2} barSize={60}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#8c8fa3' }} axisLine={false} tickLine={false} />
                            <YAxis domain={[0, 500]} tick={{ fontSize: 12, fill: '#8c8fa3' }} axisLine={false} tickLine={false} />
                            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                {data2.map((entry, index) => (
                                    <Cell key={`bar-${index}`} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            </div>
        </div>
    );
};

export default TransactionCampaign;
